import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button, TextField, Paper, Typography, Box, Alert, CircularProgress } from "@mui/material";
import { Company } from "../models/Company";
import { createCompany } from "../services/userService";
import { colors, fonts, buttonStyles, radius } from "../utils/colors";

interface CreateCompanyDialogProps {
  onSuccess?: () => void;
  companies?: Company[];
}

const CreateCompanyDialog: React.FC<CreateCompanyDialogProps> = ({ onSuccess, companies }) => {
  const { getAccessToken, isLoggedIn, userId, isSuperAdmin } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isDuplicate = companies?.some(
    c => c.companyName?.toLowerCase().trim() === companyName.toLowerCase().trim()
  ) ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      setError("Company name is required");
      return;
    }

    if (isDuplicate) {
      setError("A company with this name already exists");
      return;
    }

    if (!userId) {
      setError("User ID not available");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await createCompany(
        {
          companyName: companyName.trim(),
          adminUserId: parseInt(userId, 10)
        },
        getAccessToken
      );

      if (result.ok) {
        setSuccess(true);
        setCompanyName("");
        
        // Call success callback if provided
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1000);
        } else {
          setTimeout(() => setSuccess(false), 5000);
        }
      } else {
        setError(result.error || "Failed to create company");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <Box sx={{ maxWidth: 600, margin: "0 auto", padding: 3 }}>
        <Alert severity="warning">Please sign in to create companies.</Alert>
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Box sx={{ maxWidth: 600, margin: "0 auto", padding: 3 }}>
        <Alert severity="error">Access Denied. Only Super Administrators can create companies.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, margin: "0 auto", padding: 3 }}>
      <Paper elevation={0} sx={{ padding: 4, borderRadius: `${radius.lg}px`, border: `1px solid ${colors.lightGray}` }}>
        <Typography variant="body2" sx={{ mb: 3, color: colors.mediumGray, fontFamily: fonts.main }}>
          Create a new company in the system. Companies can have multiple users and manage their own request periods.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Company created successfully!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Company Name"
            name="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            margin="normal"
            disabled={loading}
            error={isDuplicate && companyName.trim().length > 0}
            helperText={isDuplicate && companyName.trim().length > 0 ? "A company with this name already exists" : "The name of the company (required)"}
          />

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !companyName.trim() || isDuplicate}
              startIcon={loading && <CircularProgress size={20} />}
              fullWidth
              sx={buttonStyles.secondary}
            >
              {loading ? "Creating..." : "Create Company"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateCompanyDialog;
